import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { subscribeToAuthenticatedApiActivity, UserDto, authAPI, usersAPI } from '../services/api';
import { clearSessionTokens, expireClientSession, getAccessToken, storeSessionTokens } from '../services/clientSession';
import { getInactivityTimeoutMs } from '../config/session';
import { changeAppLanguage } from '../i18n';
import { persistLocalePreference } from '../i18n/preferences';

interface AuthContextType {
  user: UserDto | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, firstName: string, lastName: string, phone?: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  updateCurrentUser: (updates: Partial<UserDto>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
const SESSION_REVOKE_TIMEOUT_MS = 1500;
const buildFallbackAuthenticatedUser = (overrides?: Partial<UserDto>): UserDto => ({
  id: '',
  email: '',
  firstName: '',
  lastName: '',
  ...overrides,
});

const normalizeUserProfileResponse = (payload: any): UserDto | null => {
  if (!payload) {
    return null;
  }

  const user = payload.data ?? payload;
  if (!user || typeof user !== 'object') {
    return null;
  }

  if (!user.id || !user.email) {
    return null;
  }

  return {
    id: user.id,
    email: user.email,
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    phone: user.phone,
    lastOpenedTreeId: user.lastOpenedTreeId,
    systemRole: user.systemRole,
    canAllowLivingPersonPublicDisplay: user.canAllowLivingPersonPublicDisplay,
    preferredLocale: user.preferredLocale,
  };
};

const parseBackendInactivityTimeoutMs = (payload: any): number | null => {
  const data = payload?.data ?? payload;
  const timeoutMinutes = Number(data?.idleTimeoutMinutes);

  if (!Number.isFinite(timeoutMinutes) || timeoutMinutes <= 0) {
    return null;
  }

  return timeoutMinutes * 60 * 1000;
};

const extractTokenResponse = (payload: any) => {
  const data = payload?.data ?? payload;

  let accessToken = data?.accessToken;
  let refreshToken = data?.refreshToken;
  const userData = data?.user;

  // Backward compatibility for older BFF responses that serialized token payload as a JSON string.
  if (typeof accessToken === 'string' && accessToken.startsWith('{')) {
    try {
      const parsed = JSON.parse(accessToken);
      accessToken = parsed.accessToken;
      refreshToken = parsed.refreshToken ?? refreshToken;
    } catch (error) {
    }
  }

  return {
    accessToken,
    refreshToken,
    userData,
  };
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [inactivityTimeoutMs, setInactivityTimeoutMs] = useState(() => getInactivityTimeoutMs());
  const inactivityTimerRef = useRef<number | null>(null);

  useEffect(() => {
    let isMounted = true;

    authAPI.getSessionConfig()
      .then((response) => {
        const timeoutMs = parseBackendInactivityTimeoutMs(response.data);
        if (isMounted && timeoutMs) {
          setInactivityTimeoutMs(timeoutMs);
        }
      })
      .catch(() => {
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const stopInactivityTracking = useCallback(() => {
    if (inactivityTimerRef.current !== null) {
      window.clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  const expireSession = useCallback(async (reason: 'expired' | 'unauthorized' = 'expired') => {
    stopInactivityTracking();

    if (reason === 'expired' && getAccessToken()) {
      await Promise.race([
        authAPI.logout().catch(() => undefined),
        new Promise<void>((resolve) => {
          window.setTimeout(resolve, SESSION_REVOKE_TIMEOUT_MS);
        }),
      ]);
    }

    clearSessionTokens();
    setUser(null);
    expireClientSession(reason);
  }, [stopInactivityTracking]);

  const resetInactivityTimer = useCallback(() => {
    stopInactivityTracking();
    inactivityTimerRef.current = window.setTimeout(() => {
      void expireSession();
    }, inactivityTimeoutMs);
  }, [expireSession, inactivityTimeoutMs, stopInactivityTracking]);

  useEffect(() => {
    if (isLoading || !user) {
      stopInactivityTracking();
      return;
    }

    const handleActivity = () => {
      resetInactivityTimer();
    };

    resetInactivityTimer();
    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, true);
    });

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity, true);
      });
      stopInactivityTracking();
    };
  }, [isLoading, resetInactivityTimer, stopInactivityTracking, user]);

  const checkAuth = useCallback(async () => {
    try {
      const token = getAccessToken();
      if (token) {
        try {
          const response = await usersAPI.getProfile();
          const normalizedUser = normalizeUserProfileResponse(response.data);
          if (normalizedUser) {
            setUser(normalizedUser);
          } else {
            // Keep the session active when profile hydration fails, but do not infer roles.
            // Protected routes must continue to deny privileged access until the role is confirmed.
            setUser(buildFallbackAuthenticatedUser());
          }
        } catch (profileError) {
          // Token exists in storage and isn't expired yet. Preserve basic authenticated state,
          // but keep the fallback user role-less so admin routes remain deny-by-default.
          setUser(buildFallbackAuthenticatedUser());
        }
      }
    } catch (error) {
      await expireSession('unauthorized');
    } finally {
      setIsLoading(false);
    }
  }, [expireSession]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (!user?.preferredLocale) {
      return;
    }

    persistLocalePreference(user.preferredLocale);
    void changeAppLanguage(user.preferredLocale);
  }, [user?.preferredLocale]);

  useEffect(() => {
    if (isLoading || !user) {
      return;
    }

    return subscribeToAuthenticatedApiActivity(() => {
      resetInactivityTimer();
    });
  }, [isLoading, resetInactivityTimer, user]);

  const updateCurrentUser = (updates: Partial<UserDto>) => {
    setUser((currentUser) => {
      if (!currentUser) {
        return currentUser;
      }

      return {
        ...currentUser,
        ...updates,
      };
    });
  };

  const hydrateUserProfile = async (fallbackUser: UserDto) => {
    try {
      const response = await usersAPI.getProfile();
      const normalizedUser = normalizeUserProfileResponse(response.data);
      if (normalizedUser) {
        setUser(normalizedUser);
        return;
      }
    } catch (error) {
    }

    setUser(fallbackUser);
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await authAPI.login({ email, password });
      const { accessToken, refreshToken, userData } = extractTokenResponse(response.data);

      if (accessToken) {
        storeSessionTokens({ accessToken, refreshToken });
        await hydrateUserProfile(userData || buildFallbackAuthenticatedUser({ email }));
      } else {
        throw new Error('No access token in response');
      }
    } catch (error) {
      throw new Error('Login failed');
    }
  };

  const register = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    phone?: string
  ) => {
    try {
      const response = await authAPI.register({ email, password, firstName, lastName, phone });
      const { accessToken, refreshToken, userData } = extractTokenResponse(response.data);

      if (accessToken) {
        storeSessionTokens({ accessToken, refreshToken });
        await hydrateUserProfile(userData || buildFallbackAuthenticatedUser({ email, firstName, lastName }));
      }
    } catch (error) {
      throw new Error('Registration failed');
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } finally {
      stopInactivityTracking();
      clearSessionTokens();
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        logout,
        checkAuth,
        updateCurrentUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
